// Widget_CashAtBank — task 4.4 of the dashboard-redesign spec (Requirement 8).
//
// KPI tile that surfaces cash held across every bank account in the current
// `BuildingScope`, split into Trust and Operating sub-totals per Constitution
// Rule 2 (bank accounts are trust/operating designated per s42 LTA 1987).
//
// The widget is READ-ONLY. It:
//   1. consumes `useCashAtBank(scope)` (task 2.4) whose invariant
//      `trustPence + operatingPence === totalPence` is property-tested;
//   2. wraps rendering in `<WidgetFrame>` so the 4 lifecycle states
//      (loading, empty, ready, error) all share one visual contract; and
//   3. deep-links the ready-state total to `/dashboard/banking` — the
//      canonical bank-accounts list route (see `frontend/src/App.tsx`).
//
// Colour semantics (design-language.md §"Semantic — money"):
// cash-at-bank is receivable — money held in trust/operating for the
// tenant — so the total renders in the Owed green (`getMonetaryColour(_,
// "owed")`). A zero total renders in the neutral grey.
//
// The whole tree is wrapped in `<ErrorBoundary>` so a render-time
// exception in any sub-tree cannot escape into the surrounding page
// (Requirement 3.6). The `WidgetError` fallback carries a synthetic
// ErrorId — the boundary path is defence-in-depth; the primary error
// surface is the `error` branch of the hook's `WidgetResult`.

import React from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { WidgetFrame } from "../WidgetFrame";
import { WidgetError } from "../WidgetError";
import { useCashAtBank } from "../hooks/useCashAtBank";
import { formatMoneyGBP, getMonetaryColour } from "../formatMoney";
import type { BuildingScope, CashAtBankSummary } from "../types";

export interface WidgetCashAtBankProps {
  scope: BuildingScope;
}

/**
 * Renders the KPI tile chrome (white card + title + divider) around whatever
 * the caller passes in. Kept as a local helper so every render state
 * (skeleton, empty, ready, error) shares the same bounding box — this is
 * what stops the page reflowing between states (Requirement 3.5).
 */
function TileFrame({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      data-testid="widget-cash-at-bank"
      className="bg-white rounded-xl border h-full flex flex-col p-4"
      style={{ borderColor: "#e5e7eb" }}
    >
      <div className="text-sm font-semibold" style={{ color: "#111827" }}>
        Cash at Bank
      </div>
      <div className="mt-2 border-t" style={{ borderColor: "#e5e7eb" }} />
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

/**
 * Ready-state body: total money on top, then Trust / Operating split lines.
 *
 * The total (and only the total) is wrapped in a `<Link>` to
 * `/dashboard/banking` so a click drills through to the per-account list;
 * the sub-totals are informational only. Wrapping the retry button in a
 * `Link` would swallow the retry click, so the deep-link is scoped to the
 * ready state — the error card's `Retry` control stays outside any router
 * navigation.
 */
function CashAtBankReady({ data }: { data: CashAtBankSummary }): JSX.Element {
  const totalColour = getMonetaryColour(data.totalPence, "owed");

  return (
    <div className="flex flex-col justify-center gap-2 py-2 h-full">
      <Link
        to="/dashboard/banking"
        data-testid="cash-at-bank-link"
        className="block hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
      >
        <div
          data-testid="cash-at-bank-total"
          className="text-2xl font-bold text-center"
          style={{ color: totalColour }}
        >
          {formatMoneyGBP(data.totalPence)}
        </div>
      </Link>
      <div className="border-t" style={{ borderColor: "#e5e7eb" }} />
      <div className="text-xs space-y-1">
        <div className="flex justify-between">
          <span style={{ color: "#6b7280" }}>Trust:</span>
          <span
            data-testid="cash-at-bank-trust"
            className="font-medium"
            style={{ color: "#111827" }}
          >
            {formatMoneyGBP(data.trustPence)}
          </span>
        </div>
        <div className="flex justify-between">
          <span style={{ color: "#6b7280" }}>Operating:</span>
          <span
            data-testid="cash-at-bank-operating"
            className="font-medium"
            style={{ color: "#111827" }}
          >
            {formatMoneyGBP(data.operatingPence)}
          </span>
        </div>
      </div>
    </div>
  );
}

export function Widget_CashAtBank({
  scope,
}: WidgetCashAtBankProps): JSX.Element {
  const result = useCashAtBank(scope);

  return (
    <ErrorBoundary
      fallback={
        <TileFrame>
          <WidgetError name="Cash at Bank" errorId="render-error" />
        </TileFrame>
      }
    >
      <TileFrame>
        <WidgetFrame
          result={result}
          name="Cash at Bank"
          emptyCopy="No bank accounts"
          skeletonHeight={80}
        >
          {result.status === "ready" && <CashAtBankReady data={result.data} />}
        </WidgetFrame>
      </TileFrame>
    </ErrorBoundary>
  );
}

export default Widget_CashAtBank;
